<template>
  <v-col cols="12" sm="12" md="8" class="d-flex">
    <v-card class="elevation-1 flex-grow-1">
      <v-toolbar color="white">
        <v-toolbar-title class="text-md-subtitle-1">
          <span>Agent Leaderboard</span>
          <v-btn
            icon="fa-regular fa-circle-info"
            variant="text"
            aria-label="info"
            color="btncolor"
            @click="showToolTip"
          >
          </v-btn>
        </v-toolbar-title>
      </v-toolbar>

      <v-container class="card-container">
        <v-row v-if="initialLoad" align="center" justify="center">
          <v-col class="pt-0">
            <div class="progress-container">
              <v-progress-circular
                :size="75"
                :width="8"
                class="my-auto"
                color="primary"
                indeterminate
              />
            </div>
          </v-col>
        </v-row>
        <v-row v-else class="fill-height">
          <v-col v-show="agents.length === 0 && !initialLoad">
            <overview-agent-leaderboard-clear-component />
          </v-col>
          <v-col v-show="agents.length > 0 && !initialLoad">
            <v-row>
              <v-col>
                <v-data-table-server
                  style="width: 100%"
                  class="elevation-0 agent-leaderboard-table"
                  mobile-breakpoint="0"
                  :headers="headers"
                  :items="agents"
                  :items-length="totalAgents"
                  :loading="loading"
                  :sort-by="sortBy"
                  :must-sort="true"
                  :mobile="false"
                  @update:options="dataTableOptionsUpdate"
                  return-object
                >
                  <template v-slot:header.revenue="{ header }">
                    <span class="font-weight-regular">
                      Transaction<br />Volume
                    </span>
                  </template>
                  <template #header.maxDaysOverdue="{ header }">
                    <span class="font-weight-regular">
                      7/30 day<br />response time
                    </span>
                  </template>
                  <template #item="props">
                    <overview-agent-leaderboard-row
                      :agent="props.item"
                      @view-agent="viewAgent"
                    />
                  </template>
                  <!-- remove bottom pagination -->
                  <template #bottom></template>
                </v-data-table-server>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pt-0 px-0">
                <data-table-pagination
                  rows-type="agents"
                  :pagination="pagination"
                  :totalItems="totalAgents"
                  :rows-per-page-items="rowsPerPageItems"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-col>
</template>

<script>
import api from "@/services/api.js";
import { mapGetters, mapMutations } from "vuex";
import DataTablePagination from "@/components/console/partials/DataTablePagination";
import OverviewAgentLeaderboardRow from "@/components/console/dashboard/OverviewAgentLeaderboardRow";
import useAuthMixin from "@/mixins/auth.js";
import { defineAsyncComponent } from "vue";

export default {
  name: "console-overview-agent-leaderboard",
  components: {
    DataTablePagination,
    OverviewAgentLeaderboardRow,
    OverviewAgentLeaderboardClearComponent: defineAsyncComponent(
      () => import("./OverviewAgentLeaderboardClearComponent"),
    ),
  },
  props: {},
  watch: {
    pagination: {
      handler() {
        this.loadAgents();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("user", ["myBrokerage"]),
    ...mapGetters("auth", ["impBrokerage"]),
    viewAllAgentsButtonText() {
      return this.totalAgents > 0
        ? `View All ${this.totalAgents} Active Agents`
        : "View All Active Agents";
    },
  },
  data() {
    return {
      headers: [
        {
          title: "Name",
          value: "name",
          key: "name",
          sortable: true,
          align: "start",
          width: "40%",
        },
        {
          title: "Score",
          value: "score",
          key: "score",
          sortable: true,
          align: "end",
        },
        {
          title: "Closings",
          value: "closings",
          key: "closings",
          sortable: true,
          align: "end",
          class: "hidden-xs",
        },
        {
          title: "Trans. $",
          value: "revenue",
          key: "revenue",
          sortable: true,
          align: "end",
          class: "hidden-xs",
        },
        {
          title: "7/30 day resp. time",
          value: "maxDaysOverdue",
          key: "maxDaysOverdue",
          sortable: false,
          align: "end",
          class: "hidden-xs text-left",
        },
      ],
      dtLoad: true,
      initialLoad: true,
      loading: false,

      agents: [],
      totalAgents: 0,
      toolTip: null,
      toolTipDialog: false,
      sortBy: [{ key: "score", order: "asc" }],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        descending: true,
        sortBy: "score",
        totalItems: 0,
      },
      currentPagination: {},
      rowsPerPageItems: [],
    };
  },
  methods: {
    ...useAuthMixin(),
    ...mapMutations("user", ["setMyAgentsSort", "setMyAgentsSortDesc"]),
    dataTableOptionsUpdate(options) {
      console.log(options);

      this.sortBy = options.sortBy;
      if (this.sortBy.length > 0) {
        const sb = this.sortBy[0];
        this.pagination.sortBy = sb.key;
        this.pagination.descending = sb.order === "desc";
      }
    },
    async loadAgents() {
      if (!this.loading) {
        console.log("****** Loading agents");

        this.loading = true;
        let sort = "agentDisplayScore,DESC";

        let dir = this.pagination.descending ? "DESC" : "ASC";
        let sortBy = this.pagination.sortBy;
        let index = this.pagination.page - 1;
        let rowsPerPage = this.pagination.rowsPerPage;

        switch (sortBy) {
          case "score":
            sort = `agentDisplayScore,${dir}`;
            break;

          case "name":
            sort = `name,${dir}`;
            break;

          case "closings":
            sort = `closings,${dir}`;
            break;

          case "revenue":
            sort = `revenue,${dir}`;
            break;

          default:
            sort = "agentDisplayScore,DESC";
            break;
        }

        try {
          // let { resp } = await api.getAgents(index, rowsPerPage, sort);
          let resp = await api.agent.getAgentLeaderboard({
            pageIndex: index,
            pageSize: rowsPerPage,
            sort: sort,
          });

          if (resp.data?.content) {
            this.agents = resp.data.content;
          }
          if (resp.data && resp.data.totalElements) {
            this.totalAgents = resp.data.totalElements;
            // this.pagination.totalItems = this.totalAgents;
          }
        } catch (e) {
          console.log("******** error *************");
          console.log(e);
          this.errorCheck(e);
        } finally {
          this.loading = false;
          this.initialLoad = false;
          this.currentPagination = { ...this.pagination };
        }
      }
    },
    viewAgent(agentId) {
      if (agentId) {
        this.$router.push({
          name: "view-my-agent",
          params: {
            id: agentId,
          },
        });
      }
    },
    showToolTip() {
      this.$emit("toolTip", "OverviewAgentLeaderboardToolTip");
    },
  },
  mounted() {
    this.initialLoad = true;
    this.loadAgents();
  },
};
</script>

<style scoped>
.card-container {
  height: calc(100% - 64px);
}

.progress-container {
  width: 100%;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-leaderboard-table {
  background: transparent !important;
}

.agent-leaderboard-table :deep(table thead tr th) {
  border-bottom: none !important;
  border-top: none !important;
  border-color: #ffffff !important;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}
.agent-leaderboard-table :deep(th.active) {
  color: #4a90e2 !important;
  font-weight: 600 !important;
}

.agent-leaderboard-table :deep(table tbody tr) {
  font-size: 14px !important;
}
.agent-leaderboard-table :deep(table tbody tr td) {
  border-bottom: none !important;
  border-top: none !important;
  border-color: #ffffff !important;
  font-size: 14px !important;
}
.agent-leaderboard-table .v-table tbody tr:not(:first-child) td {
  /*padding-top: 16px !important;*/
}
.agent-leaderboard-table :deep(table tbody tr:hover) {
  background-color: #f5f5f5 !important;
}

.v-data-table {
  background: transparent;
}
.v-data-table :deep(.v-data-table-header__sort-icon) {
  font-size: unset;
  margin-left: 4px;
}

.v-data-table :deep(.v-data-table-header__content) {
  font-weight: 400;
}

/* Style specific table headers */
/* Status header */
:deep(table th:not(:last-child :first-child)) {
  padding-left: 4px !important;
}

/* Slotted header hover sort handling */
:deep(table th .header-hover:hover .header-icon) {
  opacity: 0.5 !important;
  font-size: unset;
  margin-left: 4px;
}
:deep(table th .header-hover .header-icon) {
  opacity: 0;
}
:deep(table th.v-data-table__th--sorted .header-hover .header-icon) {
  opacity: 1 !important;
  font-size: unset;
  margin-left: 4px;
}
:deep(table th.v-data-table__th--sorted:hover .header-hover .header-icon) {
  opacity: 1;
  font-size: unset;
  margin-left: 4px;
}

.v-data-table :deep(.v-data-table__th) {
  padding: 0 !important;
}
</style>
